import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ArrowUpRight, ArrowRight, Menu } from 'lucide-react';

// Deals & Strategy page header: mirrors the site navbar with Deals & Practices active.
export const DealsHeader = ({ onOpenBriefing, onOpenDispatch, onToggleMobile }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();

  const goHome = (id) => { navigate(id ? `/#${id}` : '/'); setOpenDropdown(null); };
  const goProducts = (hash = '') => { navigate(`/products${hash}`); setOpenDropdown(null); };
  const goCompany = (hash = '') => { navigate(`/company${hash}`); setOpenDropdown(null); };
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setOpenDropdown(null);
  };

  const dropdownBg = 'bg-[#090E1A]/95 backdrop-blur-2xl';

  return (
    <header className="sticky top-0 z-40 bg-[#FBFBFB]/95 backdrop-blur-xl border-b border-stone-200/80 px-6 md:px-10 lg:px-16 transition-all">
      <div className="w-full max-w-[1600px] mx-auto flex items-center justify-between h-16">

        {/* Left nav */}
        <div className="hidden lg:flex items-center gap-10">

          {/* Products */}
          <div className="relative" onMouseEnter={() => setOpenDropdown('products')} onMouseLeave={() => setOpenDropdown(null)}>
            <button onClick={() => goProducts()} className="flex items-center gap-1 text-[13px] font-medium text-stone-600 hover:text-stone-950 transition-colors py-4">
              Products <ChevronDown size={14} className={`text-stone-400 transition-transform ${openDropdown === 'products' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'products' && (
              <div className="absolute top-full -left-6 pt-2 w-[520px] z-50">
                <div className={`${dropdownBg} border border-white/15 rounded-2xl shadow-2xl p-6 text-white`}>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold block mb-4">The OS Platform</span>
                      <div className="space-y-3">
                        <button onClick={() => goProducts('#top')} className="block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="flex items-center gap-2 font-semibold text-white text-[13px]">Deal OS™<span className="text-[10px] bg-white/15 text-white/70 px-1.5 py-0.5 rounded font-medium">Core</span></div>
                          <p className="text-[12px] text-white/45 mt-0.5">Cognitive deal engine runtime</p>
                        </button>
                        <button onClick={() => goProducts('#workflows')} className="block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white text-[13px]">Workflows</div>
                          <p className="text-[12px] text-white/45 mt-0.5">End-to-end autonomous deal pipelines</p>
                        </button>
                        <button onClick={() => goProducts('#brain')} className="block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white text-[13px]">Brain</div>
                          <p className="text-[12px] text-white/45 mt-0.5">Unified transaction memory & cross-deal intelligence</p>
                        </button>
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold block mb-4">Dealmaker Products</span>
                      <div className="space-y-2.5">
                        <button onClick={() => goProducts('#studio')} className="block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white text-[13px]">Studio</div>
                          <p className="text-[12px] text-white/45">Unified dealmaker cockpit & IC deliberations</p>
                        </button>
                        <button onClick={() => goProducts('#memo')} className="block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white text-[13px]">Memo</div>
                          <p className="text-[12px] text-white/45">Audit-grade strategic decks & IC memos</p>
                        </button>
                        <button onClick={() => goProducts('#model')} className="block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white text-[13px]">Model</div>
                          <p className="text-[12px] text-white/45">Deterministic off-LLM valuation & return models</p>
                        </button>
                        <button onClick={() => goProducts('#chat')} className="block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white text-[13px]">Chat</div>
                          <p className="text-[12px] text-white/45">Cell-grounded VDR data room interrogation</p>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <button onClick={() => goProducts('#top')} className="flex items-center gap-2 text-[13px] font-semibold text-white hover:text-white/70 transition px-2.5">
                      Explore the Engine Suite <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Deals & Practices (active) */}
          <div className="relative" onMouseEnter={() => setOpenDropdown('solutions')} onMouseLeave={() => setOpenDropdown(null)}>
            <button onClick={() => scrollToId('top')} className="flex items-center gap-1 text-[13px] font-semibold text-stone-950 transition-colors py-4">
              Deals & Practices <ChevronDown size={14} className={`text-stone-400 transition-transform ${openDropdown === 'solutions' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'solutions' && (
              <div className="absolute top-full -left-12 pt-2 w-[440px] z-50">
                <div className={`${dropdownBg} border border-white/15 rounded-2xl shadow-2xl p-6 text-white`}>
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold block mb-4">By stage</span>
                      <ul className="space-y-2.5 text-[14px]">
                        <li><button onClick={() => scrollToId('lifecycle')} className="block text-left text-white hover:text-white/70 transition font-medium">Sourcing</button></li>
                        <li><button onClick={() => scrollToId('lifecycle')} className="block text-left text-white hover:text-white/70 transition font-medium">Diligence</button></li>
                        <li><button onClick={() => scrollToId('lifecycle')} className="block text-left text-white hover:text-white/70 transition font-medium">Value creation</button></li>
                        <li><button onClick={() => scrollToId('lifecycle')} className="block text-left text-white hover:text-white/70 transition font-medium">Strategic exit</button></li>
                      </ul>
                    </div>
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold block mb-4">By practice</span>
                      <ul className="space-y-2.5 text-[14px]">
                        <li><button onClick={() => scrollToId('practices')} className="block text-left text-white hover:text-white/70 transition font-medium">Venture capital</button></li>
                        <li><button onClick={() => scrollToId('practices')} className="block text-left text-white hover:text-white/70 transition font-medium">Private equity</button></li>
                        <li><button onClick={() => scrollToId('practices')} className="block text-left text-white hover:text-white/70 transition font-medium">Corporate M&A</button></li>
                        <li><button onClick={() => scrollToId('practices')} className="block text-left text-white hover:text-white/70 transition font-medium">Strategy advisory</button></li>
                        <li><button onClick={() => scrollToId('practices')} className="block text-left text-white hover:text-white/70 transition font-medium">Investment banking</button></li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <button onClick={() => onOpenBriefing('Charter')} className="flex items-center gap-2 text-[13px] font-semibold text-white hover:text-white/70 transition">
                      Deploy the platform. Or deliver the mandate <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button onClick={() => goHome('sovereignty')} className="text-[13px] font-medium text-stone-600 hover:text-stone-950 transition-colors">
            Sovereignty
          </button>
        </div>

        {/* Center brand */}
        <div className="flex items-center justify-center lg:absolute lg:left-1/2 lg:-translate-x-1/2">
          <button onClick={() => goHome()} className="text-[22px] font-bold tracking-[0.35em] text-stone-950 uppercase transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
            Frontier
          </button>
        </div>

        {/* Right nav */}
        <div className="hidden lg:flex items-center gap-10">
          <div className="relative" onMouseEnter={() => setOpenDropdown('company')} onMouseLeave={() => setOpenDropdown(null)}>
            <button onClick={() => goCompany()} className="flex items-center gap-1 text-[13px] font-medium text-stone-600 hover:text-stone-950 transition-colors py-4">
              Company <ChevronDown size={14} className={`text-stone-400 transition-transform ${openDropdown === 'company' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'company' && (
              <div className="absolute top-full right-0 pt-2 w-[440px] z-50">
                <div className={`${dropdownBg} border border-white/15 rounded-2xl shadow-2xl p-4 text-white grid grid-cols-2 gap-4`}>
                  <div className="flex flex-col justify-center gap-3 pl-1">
                    <button onClick={() => goCompany('#about')} className="text-left text-[14px] font-medium text-white/90 hover:text-white transition">About</button>
                    <button onClick={() => goCompany('#leadership')} className="text-left text-[14px] font-medium text-white/90 hover:text-white transition">Leadership</button>
                    <button onClick={() => goCompany('#systems')} className="text-left text-[14px] font-medium text-white/90 hover:text-white transition">News</button>
                    <button onClick={() => goCompany('#footprint')} className="text-left text-[14px] font-medium text-white/90 hover:text-white transition">Footprint</button>
                    <button onClick={() => onOpenBriefing('Charter')} className="text-left text-[14px] font-semibold text-white hover:text-white/70 transition">Contact</button>
                  </div>
                  <button onClick={() => (onOpenDispatch ? onOpenDispatch('IdeaSpring Capital Case Brief') : goCompany('#systems'))} className="text-left rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-3 transition group">
                    <div className="rounded-lg bg-[#060A13] border border-white/10 h-16 flex items-center justify-center mb-2.5">
                      <div className="flex items-center gap-1">
                        <span className="font-extrabold text-[13px] text-white tracking-tight">IdeaSpring</span>
                        <span className="font-light tracking-widest text-[13px] text-white/50">CAPITAL</span>
                      </div>
                    </div>
                    <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-white/50 block mb-1">Featured Dispatch</span>
                    <p className="text-[12px] font-semibold text-white leading-snug">Automating deep-tech PortCo monitoring &amp; exit positioning</p>
                  </button>
                </div>
              </div>
            )}
          </div>

          <button onClick={() => onOpenBriefing('Charter')} className="flex items-center gap-2 text-[13px] font-semibold rounded-full px-5 py-2 transition-all text-[#0F172A] border border-[#0F172A] hover:bg-[#090E1A] hover:text-white">
            Request Briefing <ArrowUpRight size={14} />
          </button>
        </div>

        {/* Mobile toggle */}
        <button className="lg:hidden p-2 text-stone-900" onClick={onToggleMobile} aria-label="Toggle Navigation Menu">
          <Menu size={24} />
        </button>
      </div>
    </header>
  );
};
